import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { storeService } from '../services/store.service';
import { LoadingSpinner } from '../components/LoadingSpinner';

export const CategoriesPage: React.FC = () => {
  const { data: categories, isLoading, error } = useQuery({
    queryKey: ['all-categories'],
    queryFn: () => storeService.getCategories(),
  });

  if (isLoading) {
    return (
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 text-center space-y-4">
        <h2 className="text-2xl font-black text-destructive tracking-tight">Error Loading Categories</h2>
        <p className="text-muted-foreground">Unable to fetch the categories at this time.</p>
        <button 
          onClick={() => window.location.reload()} 
          className="bg-primary text-white px-6 py-3 rounded-xl font-bold text-sm hover:scale-105 transition-transform shadow-lg"
        >
          Retry Connection
        </button>
      </div>
    );
  }

  const categoryList = categories || [];

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <div className="mb-10">
        <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white">Shop by Category</h1>
        <p className="text-gray-500 font-medium tracking-tight">
          Browse through {categoryList.length} categories to find what you're looking for.
        </p>
      </div>

      {categoryList.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {categoryList.map((category) => (
            <Link 
              key={category.id} 
              to={`/products?category=${category.slug}`}
              className="group relative aspect-[4/5] rounded-3xl overflow-hidden cursor-pointer shadow-xl hover:shadow-2xl transition-shadow duration-300"
            >
              {category.thumbnailUrl ? (
                <div
                  className="absolute inset-0 bg-cover bg-center group-hover:scale-110 transition-transform duration-700"
                  style={{ backgroundImage: `url("${category.thumbnailUrl}")` }}
                ></div>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-primary/80 group-hover:scale-110 transition-transform duration-700"></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-white text-xl font-black">{category.name}</h3>
                {category.description && (
                  <p className="text-white/60 text-xs font-medium tracking-wide mt-1 line-clamp-2">
                    {category.description}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="py-32 text-center space-y-6">
          <div className="size-20 bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6">
            <span className="material-symbols-outlined text-5xl text-gray-300">category</span>
          </div>
          <p className="text-2xl font-black tracking-tight text-gray-400">No categories available</p>
          <p className="text-gray-500">Check back later for new categories.</p>
        </div>
      )}
    </div>
  );
};